import * as React from 'react';
import Fuse from 'fuse.js';
import { useTranslations } from 'next-intl';
import { FunnelSimple, XCircle } from '@phosphor-icons/react';
import { styled } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';
import { TextField, IconButton } from '@unity/cloud-ui';
import { SidebarContext } from '../sidebar-provider';
import type { SidebarTreeItem, SidebarSection } from '../types';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

interface SidebarFilterProps {
  initialData: SidebarSection[];
}

export const getSidebarSectionsDepth = (sections: SidebarSection[]): number => {
  const getSidebarItemsDepth = (items: SidebarTreeItem[]): number => {
    return items.reduce((acc, item) => {
      if (item.children?.length) {
        return 1 + getSidebarItemsDepth(item.children);
      }
      return acc + 1;
    }, 1);
  };

  return sections.reduce((acc, section) => Math.max(acc, getSidebarItemsDepth(section.items)), 0);
};

const getFuseKeys = (maxDepth: number): string[] => {
  const keys = ['title'];
  for (let i = 0; i < maxDepth; i++) {
    keys.push(`items.${'children.'.repeat(i)}label`);
  }
  return keys;
};

export const SidebarFilter = (props: SidebarFilterProps) => {
  const { initialData } = props;
  const t = useTranslations();
  const { searchValue, setSearchValue, setFilteredSections, setSearchResults } = React.useContext(SidebarContext);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const maxDepth = React.useMemo(() => getSidebarSectionsDepth(initialData), [initialData]);
  const fuseKeys = React.useMemo(() => getFuseKeys(maxDepth), [maxDepth]);

  const fuse = new Fuse(initialData, {
    includeMatches: true,
    threshold: 0.3,
    shouldSort: false,
    keys: fuseKeys,
  });

  const handleSearch = (event) => {
    const { value } = event.target;
    setSearchValue(value);

    if (value.length === 0) {
      setSearchResults(null);
      setFilteredSections(initialData);
      return;
    }

    const results = fuse.search(value);
    // extract all matching sidebar items out of results
    const matches = results.reduce(
      (acc, result) => {
        // clean out undefined match values
        const sectionMatches = result.matches?.reduce<{ value: string; matchPosition?: number }[]>((acc, match) => {
          if (match.value) {
            const { matchPosition } = match.indices.reduce(
              (accIndices, index) => {
                const length = index[1] - index[0];
                if (length > accIndices.maxLength) {
                  return {
                    maxLength: length,
                    matchPosition: index[0],
                  };
                }
                return accIndices;
              },
              {
                maxLength: 0,
                matchPosition: undefined,
              },
            );

            acc.push({
              value: match.value,
              matchPosition,
            });
          }
          return acc;
        }, []);
        acc.push(...(sectionMatches ?? []));
        return acc;
      },
      [] as { value: string; matchPosition?: number }[],
    );

    setFilteredSections(results.map((result) => result.item));
    setSearchResults(matches);
  };

  const handleClear = () => {
    setSearchValue('');
    setSearchResults(null);
    setFilteredSections(initialData);
    inputRef.current?.focus();
  };

  return (
    <StyledTextField
      inputRef={inputRef}
      id="sidebar-filter"
      value={searchValue}
      onChange={handleSearch}
      placeholder={t('sidebar:filter:placeholder')}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <FunnelSimple size={16} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ width: '24px' }}>
              {!!searchValue && (
                <IconButton size="small" aria-label={t('sidebar:filter:clear')} onClick={handleClear}>
                  <XCircle weight="fill" size={16} />
                </IconButton>
              )}
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
